import type { ISegment } from "@repo/baseCloud";

import { useSegmentFilterCascaded } from "@repo/baseCloud";
import { useCallback, useEffect, useRef, useState } from "react";

import { BranchesDropdown, BrandsDropdown, ChannelsDropdown, MarketersDropdown, StatesDropdown } from "./";

export function SegmentFilters(props: Props) {
	const { onChange, onUnfocused } = props;
	const [segmentFilters, setSegmentFilters] = useState<ISegment>(() => pickSegment(props.value));
	const availableSegments = useSegmentFilterCascaded(segmentFilters);
	const userEditedRef = useRef(false);

	const segmentChanged = useCallback((value: any, name: string & keyof ISegment) => {
		userEditedRef.current = true;
		setSegmentFilters((prev) => ({ ...prev, [name]: value }));
	}, []);

	useEffect(() => {
		if (!props.value) return;
		userEditedRef.current = false;
		setSegmentFilters((prev) => {
			const next = pickSegment(props.value);
			return isSameSegment(prev, next) ? prev : next;
		});
	}, [props.value]);

	useEffect(() => {
		if (!userEditedRef.current) return;
		userEditedRef.current = false;
		onChange(segmentFilters);
	}, [segmentFilters, onChange]);

	//TODO: handle optional "All X" vs none selected just means all
	//TODO: handle auto-select if single for multi
	//Maybe if auto-selected (and only one option in master list), we just display nice text: "Brand: Spectrum"
	//TODO: setup cascading and active only etc
	return (
		<>
			<BrandsDropdown
				options={availableSegments.brands}
				loaded={availableSegments.brandsLoaded}
				value={segmentFilters.brandIds ?? []}
				changeHandler={segmentChanged}
				onUnfocused={onUnfocused}
				maxSelectedDisplay={3}
			/>
			<ChannelsDropdown
				options={availableSegments.channels}
				loaded={availableSegments.channelsLoaded}
				value={segmentFilters.channelIds ?? []}
				changeHandler={segmentChanged}
				onUnfocused={onUnfocused}
				maxSelectedDisplay={3}
			/>
			<MarketersDropdown
				options={availableSegments.marketers}
				loaded={availableSegments.marketersLoaded}
				value={segmentFilters.marketerIds ?? []}
				changeHandler={segmentChanged}
				onUnfocused={onUnfocused}
				maxSelectedDisplay={3}
			/>
			<StatesDropdown
				options={availableSegments.states}
				loaded={availableSegments.statesLoaded}
				value={segmentFilters.stateIds ?? []}
				changeHandler={segmentChanged}
				onUnfocused={onUnfocused}
				maxSelectedDisplay={3}
			/>
			<BranchesDropdown
				options={availableSegments.branches}
				loaded={availableSegments.branchesLoaded}
				value={segmentFilters.branchIds ?? []}
				changeHandler={segmentChanged}
				onUnfocused={onUnfocused}
				maxSelectedDisplay={3}
			/>
		</>
	);
}

export const emptySegmentFilters: ISegment = {
	branchIds: [],
	brandIds: [],
	channelIds: [],
	marketerIds: [],
	stateIds: [],
};

const SEGMENT_KEYS = [
	"brandId",
	"channelId",
	"marketerId",
	"stateId",
	"branchId",
	"brandIds",
	"channelIds",
	"marketerIds",
	"stateIds",
	"branchIds",
	"showInactiveOffices",
	"assignedUserIds",
] as const satisfies readonly (keyof ISegment)[];

function pickSegment(value?: ISegment): ISegment {
	const picked: ISegment = { ...emptySegmentFilters };
	if (!value) return picked;

	for (const key of SEGMENT_KEYS) {
		if (value[key] !== undefined) Object.assign(picked, { [key]: value[key] });
	}

	return picked;
}

function isSameSegment(a: ISegment, b: ISegment) {
	return SEGMENT_KEYS.every((key) => {
		const left = a?.[key];
		const right = b?.[key];

		if (Array.isArray(left) && Array.isArray(right))
			return left.length === right.length && left.every((item, i) => item === right[i]);

		return left === right;
	});
}

interface Props {
	onChange: (filters: ISegment) => void;
	value?: ISegment;
	onUnfocused?: (event?: React.FocusEvent<any>) => void;
}
